import React, { useMemo } from 'react'
import { Badge } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { ItemState } from '../../models'
import { StateListType } from '../../shared/filter'
import { useListedItemBadgesStyles } from './listed-item-badges.styles'

export type ListedItemConditionBadgeProps = {
  content: number
}

export const ListedItemConditionBadge: React.FC<ListedItemConditionBadgeProps> = ({
  content,
}) => {
  const { t } = useTranslation()
  const classes = useListedItemBadgesStyles()
  const isUsed = Boolean(content)

  return (
    <Badge
      overlap="rectangular"
      badgeContent={
        isUsed
          ? t('listedItemCard.condition.used')
          : t('listedItemCard.condition.new')
      }
      color={isUsed ? 'primary' : 'default'}
      className={[classes.badgeBlock, !isUsed && classes.badgeBlockNew]
        .filter(Boolean)
        .join(' ')}
      style={{ visibility: 'hidden' }}
    />
  )
}

export type ListedItemStateBadgeProps = {
  content: number | string | undefined | null
}

export const ListedItemStateBadge: React.FC<ListedItemStateBadgeProps> = ({
  content,
}) => {
  const { t } = useTranslation()
  const classes = useListedItemBadgesStyles()

  const stateList = useMemo<StateListType>(
    () => [
      { state: t('filter.state.draft'), value: ItemState.Draft },
      { state: t('filter.state.active'), value: ItemState.Active },
      {
        state: t('filter.state.sellPending'),
        value: ItemState.SellPending,
      },
      { state: t('filter.state.sold'), value: ItemState.Sold },
      { state: t('filter.state.unlisted'), value: ItemState.Unlisted },
    ],
    [t]
  )

  if (content == null) {
    return null
  }

  const state =
    typeof content === 'string' ? parseInt(content, 10) : Number(content)
  if (Number.isNaN(state)) {
    return null
  }

  if (!(state || state === ItemState.Draft)) {
    return null
  }

  const label = stateList.find((item) => item.value === state)?.state ?? ''
  const colorClass = {
    [ItemState.Draft]: classes.badgeBlockDraft,
    [ItemState.Active]: classes.badgeBlockActive,
    [ItemState.SellPending]: classes.badgeBlockSellPending,
    [ItemState.Sold]: classes.badgeBlockSold,
    [ItemState.Unlisted]: classes.badgeBlockUnlisted,
  }[state as ItemState]

  if (!label || !colorClass) {
    return null
  }

  return (
    <Badge
      overlap="rectangular"
      badgeContent={label}
      className={[classes.badgeBlock, colorClass].join(' ')}
    />
  )
}

export type ListedItemPurchaseBadgeProps = {
  content: boolean
}

export const ListedItemPurchaseBadge: React.FC<ListedItemPurchaseBadgeProps> = ({
  content,
}) => {
  const classes = useListedItemBadgesStyles()

  if (!content) {
    return null
  }

  return (
    <Badge
      overlap="rectangular"
      badgeContent="Bought"
      className={[classes.badgeBlock, classes.badgeBlockSold].join(' ')}
    />
  )
}
